import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { Bell, CalendarClock, Users, Mail, Check } from "lucide-react";
import { api } from "@/lib/api";

const ICON = { deadline: CalendarClock, invite: Mail, party: Users };

export function NotificationBell() {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState([]);
  const [unread, setUnread] = useState(0);
  const ref = useRef(null);

  const load = () => api.notifications().then((d) => { setItems(d.notifications || []); setUnread(d.unread || 0); }).catch(() => {});
  useEffect(() => { load(); const t = setInterval(load, 60000); return () => clearInterval(t); }, []);
  useEffect(() => {
    const onClick = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  const toggle = async () => {
    const next = !open;
    setOpen(next);
    if (next && unread > 0) {
      setUnread(0); // optimistic
      try {
        await api.markNotificationsRead(null);
      } catch {
        load(); // reconcile true unread count on failure instead of hiding it
      }
    }
  };

  const go = (n) => { setOpen(false); if (n.link) navigate(n.link); };

  return (
    <div className="relative" ref={ref}>
      <button onClick={toggle} className="relative w-9 h-9 sm:w-10 sm:h-10 grid place-items-center text-bone/90 hover:text-blaze transition-colors" aria-label="Notifications" data-testid="notif-bell">
        <Bell size={19} />
        {unread > 0 && <span className="absolute top-1.5 right-1.5 min-w-[16px] h-4 px-1 bg-blaze text-white text-[10px] font-bold rounded-full grid place-items-center" data-testid="notif-badge">{unread}</span>}
      </button>
      <AnimatePresence>
        {open && (
          <motion.div initial={{ opacity: 0, y: -8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -8 }}
            className="absolute right-0 mt-2 w-80 max-w-[calc(100vw-24px)] max-h-[70vh] overflow-auto bg-[#14140f] border border-white/10 rounded-xl shadow-2xl z-50" data-testid="notif-panel">
            <div className="px-4 py-3 border-b border-white/10 flex items-center justify-between">
              <span className="hr-overline text-blaze text-[11px]">Notifications</span>
            </div>
            {items.length === 0 ? (
              <p className="text-bone/40 text-sm px-4 py-8 text-center" data-testid="notif-empty">You&apos;re all caught up.</p>
            ) : items.map((n) => {
              const Icon = ICON[n.type] || Bell;
              return (
                <button key={n.id} onClick={() => go(n)} className={`w-full text-left px-4 py-3 flex gap-3 hover:bg-white/5 transition-colors border-b border-white/5 ${n.read ? "opacity-60" : ""}`} data-testid={`notif-${n.id}`}>
                  <Icon size={16} className="text-blaze shrink-0 mt-0.5" />
                  <div className="min-w-0">
                    <p className="text-bone text-sm font-medium leading-snug">{n.title}</p>
                    {n.body && <p className="text-bone/50 text-xs mt-0.5 truncate">{n.body}</p>}
                  </div>
                  {!n.read && <span className="w-2 h-2 rounded-full bg-blaze shrink-0 mt-1.5 ml-auto" />}
                </button>
              );
            })}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
